import React from "react";
import { signupForEmailOffers } from "tsi-common-react/src/api/user";
import { config } from "tsi-common-react/src/config";
import { CaptchaLibrary } from "tsi-common-react/src/forms/Captcha/index";
import { updateGTMUserHash } from "tsi-common-react/src/utils/analytics";
import { useStaticURL } from "tsi-common-react/src/utils/urls";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

const TRUE = "true";
const LOCAL_STORAGE_KEY = "email-signup-modal-closed";

const getHasBeenClosed = (): boolean => {
    const window = getWindow();
    if (!window) return false;

    try {
        return window.localStorage[LOCAL_STORAGE_KEY] === TRUE;
    } catch (e) {
        console.error(e);
    }
    return false;
};

const saveHasBeenClosedToStorage = (): void => {
    const window = getWindow();
    if (!window) return;

    try {
        window.localStorage[LOCAL_STORAGE_KEY] = TRUE;
    } catch (e) {
        console.error(e);
    }
};

const handleSubmit = (
    email: string,
    setIsCompleted: (value: boolean) => void,
    setErrors: (errors: string[]) => void,
    closeModal: () => void,
): void => {
    // Update USER_HASH in the datalayer
    updateGTMUserHash(email);

    signupForEmailOffers(email)
        .then(() => {
            setIsCompleted(true);

            setTimeout(() => {
                closeModal();
            }, 800);
        })
        .catch((err) => {
            // TODO(i18n): Use t tagged template: errors: err.response.body.email || [t`An unexpected error occurred.`]
            setErrors(
                err.response.body.email || ["An unexpected error occured."],
            );
        });
};

export const EmailSignupModal = (): React.ReactNode => {
    const staticURL = useStaticURL();
    const [hasBeenClosed, setHasBeenClosed] = React.useState<boolean>(() =>
        getHasBeenClosed(),
    );
    const [email, setEmail] = React.useState<string>("");
    const [errors, setErrors] = React.useState<string[]>([]);
    const [isCompleted, setIsCompleted] = React.useState<boolean>(false);

    const closeModal = () => {
        saveHasBeenClosedToStorage();
        setHasBeenClosed(true);
    };

    const onEmailChange = (e: React.FormEvent<HTMLInputElement>): void => {
        setEmail(e.currentTarget.value);
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
        event.preventDefault();
        handleSubmit(email, setIsCompleted, setErrors, closeModal);
    };

    if (hasBeenClosed) {
        return null;
    }

    let emailSignupElem: React.JSX.Element | null = null;

    if (isCompleted) {
        emailSignupElem = (
            <div className="email-sign-up email-sign-up--no-bottom-margin l-capped-width">
                {/* TODO(i18n): Use t tagged template: {t`Thank you for subscribing!`} */}
                Thank you for subscribing!
            </div>
        );
    } else {
        emailSignupElem = (
            <div className="email-sign-up email-sign-up--no-bottom-margin l-capped-width">
                <form className="email-sign-up__form" onSubmit={onSubmit}>
                    <div className="email-sign-up__email email-sign-up__field">
                        {/* TODO(i18n): Use t tagged template for placeholder: placeholder={t`Enter email to get offers`} */}
                        <input
                            className="email-sign-up__input email-sign-up__input--white"
                            placeholder="Enter email to get offers"
                            type="email"
                            required={true}
                            value={email}
                            onChange={onEmailChange}
                        />
                    </div>
                    <div className="email-sign-up__email email-sign-up__submit al-email-signup__submit al-modal-email-signup__submit">
                        {/* TODO(i18n): Use t tagged template for value: value={t`SUBMIT`} */}
                        <input
                            className="button email-sign-up__button email-sign-up__button--ming-blue"
                            type="submit"
                            value="SUBMIT"
                        />
                        <div className="email-sign-up__diagonal email-sign-up__diagonal--ming-blue"></div>
                    </div>
                </form>
                {errors.map((msg, i) => {
                    return (
                        <p key={i} className="email-sign-up__error">
                            {msg}
                        </p>
                    );
                })}
            </div>
        );
    }

    return (
        <div className="l-full-width home-email-subscription">
            {config.get("ENABLE_EMAIL_SIGNUP_CAPTCHA") && <CaptchaLibrary />}
            <div className="l-capped-width">
                <div className="home-email-subscription__inner">
                    <div className="home-email-subscription__image">
                        <img
                            className="responsive-img home-email-subscription__image-item"
                            src={`${staticURL}img/home-page/module/home-modal.jpg`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                    <div className="home-email-subscription__content">
                        <div className="home-email-subscription__pre-header">
                            {/* TODO(i18n): Use t tagged template: {t`limited time only`} */}
                            limited time only
                        </div>
                        <div className="home-email-subscription__header">
                            {/* TODO(i18n): Use jt to keep sentence together with interpolated component: {jt`Get a ${freeText} TEMPUR-Cloud Pillow with every topper purchase`} */}
                            Get a{" "}
                            <span className="home-email-subscription__header--highlight">
                                <b>FREE</b> TEMPUR-Cloud Pillow
                            </span>{" "}
                            with every topper purchase
                        </div>
                        <div className="home-email-subscription__subscription-form">
                            {emailSignupElem}
                        </div>
                        <div
                            className="home-email-subscription__message"
                            onClick={closeModal}
                        >
                            {/* TODO(i18n): Use t tagged template: {t`Shop Now`} */}
                            Shop Now
                        </div>
                    </div>
                    <div
                        className="home-email-subscription__close"
                        onClick={closeModal}
                    >
                        <img
                            className="responsive-img"
                            src={`${staticURL}img/home-page/module/home-modal-close.jpg`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
